/**
 * VOY-73: Booking Error Card Component
 * 
 * Displays error messages with user-friendly explanations and next steps
 */

import { XCircle, AlertTriangle, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { getErrorInfo, extractErrorCode, type ErrorCode } from '@/constants/ratehawkStatusCodes'
import { Link } from 'react-router-dom'

interface BookingErrorCardProps {
  error: Error | string | { message?: string; error?: string; code?: string } | unknown
  errorCode?: ErrorCode | string
  onRetry?: () => void
  className?: string
}

export function BookingErrorCard({ 
  error, 
  errorCode,
  onRetry,
  className = '' 
}: BookingErrorCardProps) {
  // Extract error code if not provided
  const code = errorCode || extractErrorCode(error)
  const errorInfo = getErrorInfo(code)

  // Get error message from error object or use mapped message
  const errorMessage = typeof error === 'string' 
    ? error 
    : (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string')
      ? error.message
      : (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string')
        ? error.error
        : errorInfo.message

  return (
    <Card className={`border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800 ${className}`}>
      <CardHeader className="text-center">
        <div className="flex justify-center mb-4">
          <XCircle className="h-16 w-16 text-red-600 dark:text-red-400" />
        </div>
        <CardTitle className="text-2xl text-red-800 dark:text-red-200">
          {errorInfo.title}
        </CardTitle>
        <p className="text-red-700 dark:text-red-300 mt-2">
          {errorMessage}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>What happened?</AlertTitle>
          <AlertDescription>
            {errorInfo.description}
          </AlertDescription>
        </Alert>

        {errorInfo.nextSteps.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-lg p-4 space-y-2">
            <h4 className="font-semibold text-sm text-red-800 dark:text-red-200">
              What can you do?
            </h4>
            <ul className="space-y-1.5">
              {errorInfo.nextSteps.map((step, index) => (
                <li key={index} className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          {errorInfo.canRetry && onRetry && (
            <Button 
              onClick={onRetry}
              variant="default"
              className="flex-1"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {errorInfo.retryAction || 'Try Again'}
            </Button>
          )}
          {errorInfo.supportContact && (
            <Button 
              asChild
              variant="outline"
              className="flex-1"
            >
              <Link to="/support">
                Contact Support
              </Link>
            </Button>
          )}
          {!errorInfo.canRetry && !errorInfo.supportContact && (
            <Button 
              asChild
              variant="outline"
              className="flex-1"
            >
              <Link to="/">
                Back to Search
              </Link>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

